<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { notificationStore } from '$lib/stores/notifications';
  import { preferencesStore } from '$lib/stores/preferences';
  import { requestNotificationPermission } from '$lib/services/notifications';
  import { subscribePush } from '$lib/services/push';
  import { downloadBackup, importBackup } from '$lib/services/backup';
  import { tasksStore } from '$lib/stores/tasks';
  import { plansStore } from '$lib/stores/plans';
  import { historyStore } from '$lib/stores/history';
  import { trapDialogFocus } from './dialogFocus';
  import type { ThemeId } from '$lib/domain/types';
  import { DEFAULT_THEME, THEME_PRESETS } from '$lib/domain/preferences';

  const dispatch = createEventDispatcher();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
  const vapidKey = import.meta.env.VITE_PUSH_PUBLIC_KEY ?? '';

  const soundPacks = [
    { id: 'aurora', label: 'Aurora' },
    { id: 'dusk', label: 'Dusk' },
    { id: 'halo', label: 'Halo' }
  ];
  const soundMap: Record<string, string> = {
    aurora: '/sounds/aurora-chime.wav?v=2',
    dusk: '/sounds/dusk-bell.wav?v=2',
    halo: '/sounds/halo-soft.wav?v=2'
  };

  let theme: ThemeId = DEFAULT_THEME;
  let settings = {
    enabled: true,
    volume: 0.7,
    soundPack: 'aurora',
    playOn: { workComplete: true, breakComplete: true, planStart: true }
  };
  let feedback = '';
  let previewAudio: HTMLAudioElement | null = null;
  let backupInput: HTMLInputElement;

  const unsubNotifications = notificationStore.subscribe((value) => (settings = value));
  const unsubPreferences = preferencesStore.subscribe((value) => (theme = value.theme));

  $: selectedTheme = THEME_PRESETS.find((preset) => preset.id === theme) ?? THEME_PRESETS[0];

  const save = async () => {
    settings = {
      ...settings,
      volume: Math.min(Math.max(Number(settings.volume) || 0, 0), 1)
    };
    await notificationStore.setSettings(settings);
    await preferencesStore.setTheme(theme);
    dispatch('close');
  };

  const playSample = async (pack: string) => {
    const volume = Math.min(Math.max(Number(settings.volume) || 0, 0), 1);
    const label = soundPacks.find((item) => item.id === pack)?.label ?? 'sound';
    if (volume === 0) {
      feedback = 'Sound preview is muted. Raise the volume to hear it.';
      return;
    }

    if (!previewAudio) {
      feedback = 'Sound preview is still loading. Try clicking Play again.';
      return;
    }

    previewAudio.pause();
    previewAudio.src = soundMap[pack] ?? soundMap.aurora;
    previewAudio.volume = volume;
    previewAudio.currentTime = 0;
    previewAudio.load();

    try {
      await previewAudio.play();
      feedback = `Playing ${label} preview.`;
    } catch (error) {
      const isBlocked = error instanceof DOMException && error.name === 'NotAllowedError';
      feedback = isBlocked
        ? 'Browser blocked the sound preview. Click Play again in this tab.'
        : 'Sound preview could not play.';
    }
  };

  const enableNotifications = async () => {
    feedback = '';
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      await subscribePush(apiBaseUrl, vapidKey);
      feedback = 'Notifications enabled for this browser.';
    } else {
      feedback = 'Notification permission was not granted.';
    }
  };

  const handleExport = async () => {
    await downloadBackup();
  };

  const handleImport = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    try {
      await importBackup(input.files[0]);
      await Promise.all([
        tasksStore.load(),
        plansStore.load(),
        historyStore.load(),
        preferencesStore.load(),
        notificationStore.load()
      ]);
      feedback = 'Backup imported.';
    } catch (error) {
      feedback = error instanceof Error ? error.message : 'Backup import failed.';
    }
    input.value = '';
  };

  onDestroy(() => {
    previewAudio?.pause();
    unsubNotifications();
    unsubPreferences();
  });
</script>

<svelte:window on:keydown={(event) => event.key === 'Escape' && dispatch('close')} />

<div class="modal-backdrop">
  <div class="modal settings-sheet" role="dialog" aria-modal="true" aria-labelledby="settings-title" data-testid="settings-modal" use:trapDialogFocus>
    <audio bind:this={previewAudio} preload="auto" aria-hidden="true"></audio>
    <div class="panel-header sheet-header">
      <div>
        <p class="kicker">Settings</p>
        <h3 id="settings-title">Control Center</h3>
        <p class="sheet-subtitle">Tune themes, reminders, backup, and local-first behavior.</p>
      </div>
      <button class="button ghost" on:click={() => dispatch('close')}>Close</button>
    </div>

    <div class="sheet-scroll">
      <div class="panel-section">
        <div class="section-heading">
          <div>
            <div class="kicker">Theme Gallery</div>
            <strong>{selectedTheme.name}</strong>
          </div>
          <span class="tag">Default: Liquid Light</span>
        </div>
        <div class="theme-gallery" role="radiogroup" aria-label="Theme Gallery">
          {#each THEME_PRESETS as preset}
            <button
              class="theme-card"
              class:active={theme === preset.id}
              style:--theme-accent={preset.accent}
              role="radio"
              aria-checked={theme === preset.id}
              on:click={() => (theme = preset.id)}
            >
              <span class="theme-card__swatch" aria-hidden="true"></span>
              <span>
                <strong>{preset.name}</strong>
                <small>{preset.description}</small>
              </span>
            </button>
          {/each}
        </div>
      </div>

      <div class="panel-section">
        <div class="kicker">Notification sound packs</div>
        <div class="audio-grid">
          {#each soundPacks as pack}
            <div class="audio-item">
              <div>
                <strong>{pack.label}</strong>
                <div class="notice">Curated chime set</div>
              </div>
              <div class="inline-cluster compact">
                <button class="button ghost" aria-label={`Play ${pack.label} preview`} on:click={() => playSample(pack.id)}>Play</button>
                <input
                  type="radio"
                  name="soundPack"
                  value={pack.id}
                  checked={settings.soundPack === pack.id}
                  aria-label={`${pack.label} sound pack`}
                  on:change={() => (settings = { ...settings, soundPack: pack.id })}
                />
              </div>
            </div>
          {/each}
        </div>
      </div>

      <div class="panel-section">
        <div class="kicker">Sound volume</div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={settings.volume}
          aria-label="Sound volume"
          on:input={(e) => (settings = { ...settings, volume: Number((e.target as HTMLInputElement).value) })}
        />
      </div>

      <div class="panel-section">
        <div class="kicker">Enable sounds for</div>
        <label class="toggle">
          <input
            type="checkbox"
            checked={settings.playOn.workComplete}
            on:change={(e) =>
              (settings = { ...settings, playOn: { ...settings.playOn, workComplete: (e.target as HTMLInputElement).checked } })
            }
          />
          Work completion
        </label>
        <label class="toggle">
          <input
            type="checkbox"
            checked={settings.playOn.breakComplete}
            on:change={(e) =>
              (settings = { ...settings, playOn: { ...settings.playOn, breakComplete: (e.target as HTMLInputElement).checked } })
            }
          />
          Break completion
        </label>
        <label class="toggle">
          <input
            type="checkbox"
            checked={settings.playOn.planStart}
            on:change={(e) =>
              (settings = { ...settings, playOn: { ...settings.playOn, planStart: (e.target as HTMLInputElement).checked } })
            }
          />
          Plan start
        </label>
      </div>

      <div class="panel-section">
        <label class="toggle">
          <input
            type="checkbox"
            checked={settings.enabled}
            on:change={(e) => (settings = { ...settings, enabled: (e.target as HTMLInputElement).checked })}
          />
          Enable notification sounds
        </label>
        <button class="button secondary" on:click={enableNotifications}>Enable push notifications</button>
        <div class="notice">Push subscription storage is beta; closed-app reminder delivery is not guaranteed yet.</div>
      </div>

      <div class="panel-section">
        <div class="kicker">Backup</div>
        <div class="inline-cluster">
          <button class="button secondary" on:click={handleExport}>Export backup</button>
          <button class="button ghost" on:click={() => backupInput?.click()}>Import backup</button>
          <input
            bind:this={backupInput}
            class="sr-only"
            type="file"
            accept="application/json"
            aria-label="Import backup file"
            on:change={handleImport}
          />
        </div>
        <div class="notice">Import replaces local data and queues a sync push.</div>
      </div>

      {#if feedback}
        <div class="notice" role="status" aria-live="polite">{feedback}</div>
      {/if}
    </div>

    <div class="modal-actions">
      <button class="button ghost" on:click={() => dispatch('close')}>Cancel</button>
      <button class="button" on:click={save}>Save settings</button>
    </div>
  </div>
</div>
